import { LowerCasePipe } from '@angular/common';
import { Component, computed, effect } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { CaptionComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/caption/caption.component';
import { IKpiCard } from '@cta-analytics-details/cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { EDeliverableType } from '@enums/campaign.enum';
import { ICaptionAnalytics } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { AnalyticsDetailsUtilitiesService } from '@utilities/analytics-details-utilities.service';
import { TextPostAnalyticsBaseDirective } from '../text-post-analytics-base/text-post-analytics-base.directive';

@Component({
  imports: [TranslateModule, MatButtonModule, CaptionComponent, LowerCasePipe],
  providers: [DeliverableFindingsStore],
  selector: 'app-caption-post-analytics',
  templateUrl: './caption-post-analytics.component.html',
})
export class CaptionPostAnalyticsComponent extends TextPostAnalyticsBaseDirective<undefined, ICaptionAnalytics> {
  public override readonly deliverableType = EDeliverableType.caption;
  public override readonly kpiCardData: IKpiCard = {
    icon: 'view.svg',
    id: 'talkingPoint-count',
    title: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.talkingPoint.totalCount'),
    value: 0,
  };

  public override readonly currentDeliverable = computed(() => {
    const deliverableId = this.deliverableId();
    const executionAnalytics = this.executionAnalyticsWithFindings();

    if (deliverableId && executionAnalytics) {
      return executionAnalytics?.ctaAnalytics?.captions?.find((c) => c.deliverableId === deliverableId);
    }

    return null;
  });

  public override readonly contentData = computed(() => {
    const socialContent = this.executionAnalyticsWithFindings()?.content?.socialContent;

    if (!socialContent) {
      return null;
    }

    if ('instagramPostData' in socialContent) {
      return socialContent.instagramPostData;
    }

    if ('instagramReelData' in socialContent) {
      return socialContent.instagramReelData;
    }

    if ('instagramStoryData' in socialContent) {
      return socialContent.instagramStoryData;
    }

    if ('tiktokData' in socialContent) {
      return socialContent.tiktokData;
    }

    return null;
  });

  constructor() {
    super();
  }

  protected override setKeywordCounts(): void {
    const caption = this.currentDeliverable();

    const keywords = caption?.keywords?.map((k) => k.keyword) ?? [];
    const captionText = this.contentData()?.caption ?? '';
    this.keywordCounts = AnalyticsDetailsUtilitiesService.getIndividualKeywordCounts(keywords, [captionText]);

    const total = Object.values(this.keywordCounts).reduce((sum, val) => sum + val, 0);
    this.kpiCardData.value = total;

    AnalyticsDetailsUtilitiesService.mapKeywordCountsToDeliverableForm(this.deliverableForm(), this.keywordCounts);
  }

  protected override setKeywordCardList(): void {
    const caption = this.currentDeliverable();

    if (caption?.keywords && caption.keywords.length) {
      const kpiCardList = caption.keywords.map((k) => {
        return {
          icon: 'keyword.svg',
          id: `${k.keyword}-kpi-card`,
          keyword: k.keyword,
          value: this.keywordCounts[k.keyword] ?? 0,
        };
      });

      this.keywordCardList.set(kpiCardList);
    }
  }

  protected override setKeywordsEffect(): void {
    effect(() => {
      const deliverableId = this.deliverableId();
      const executionAnalytics = this.executionAnalyticsWithFindings();

      if (deliverableId && executionAnalytics) {
        this.keywords = this.currentDeliverable()?.keywords?.map((k) => k.keyword) ?? [];
      }
    });
  }
}
